
/**
 * Clear all caches and unregister service workers to force a clean app update
 */
export const clearCachesAndWorkers = async (): Promise<boolean> => {
  try {
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
        console.log('Service worker unregistered');
      }
    }
    
    // Clear caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('Caches cleared');
    }
    
    return true;
  } catch (error) {
    console.error('Error clearing caches and workers:', error);
    return false;
  }
};

/**
 * Check if a new service worker is waiting to be activated
 */
export const checkForUpdate = async (): Promise<boolean> => {
  if (!('serviceWorker' in navigator)) {
    return false;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    return Boolean(registration.waiting);
  } catch (error) {
    console.error('Error checking for updates:', error);
    return false;
  }
};

/**
 * Force the waiting service worker to activate
 */
export const activateUpdate = async (): Promise<boolean> => {
  if (!('serviceWorker' in navigator)) {
    return false;
  }
  
  try {
    const registration = await navigator.serviceWorker.ready;
    if (!registration.waiting) {
      return false;
    }
    
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    return true;
  } catch (error) {
    console.error('Error activating update:', error);
    return false;
  }
};

/**
 * Register service worker for PWA functionality
 */
export const registerServiceWorker = async (): Promise<boolean> => {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service worker not supported');
    return false;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js');
    console.log('Service worker registered with scope:', registration.scope);
    return true;
  } catch (error) {
    console.error('Service worker registration failed:', error);
    return false;
  }
};

/**
 * Setup listener for offline/online status
 */
export const setupStandaloneModeListener = (): void => {
  if (typeof window !== 'undefined') {
    let isStandalone = false;
    
    // Check if app is in standalone mode (installed)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      isStandalone = true;
    }
    
    // For iOS
    if ((window.navigator as any).standalone === true) {
      isStandalone = true;
    }
    
    if (isStandalone) {
      console.log('App is running in standalone mode (installed)');
      // Here you could trigger different behavior for installed apps
    }
    
    // Listen for changes in display mode
    window.matchMedia('(display-mode: standalone)').addEventListener('change', (event) => {
      console.log('Display mode changed to:', event.matches ? 'standalone' : 'browser');
    });
  }
};

// Re-export auth listener functions from the PWA utilities
export { setupServiceWorkerAuthListener, setupPWASessionRestoration } from './pwa/authListener';
